import React from "react";
import { Link } from "react-router-dom";
import { ROUTE_NAMES } from "../../../routes/routeConstants";
import { useAuth } from "../../../provider/AuthProvider";
import { useLoginModal } from "../../../context/LoginModalContext";
import { Leaf, P } from "./shell";
import { VELLUM as T, display, mono, body, btn } from "./tokens";

/* A-04 · issued to - the structural event of the page.

   The measure changes hands: the institutions get the plate, and the scholars
   keep a standing note in the margin so their thread survives the turn. Where
   the page this replaces marked the handover with a 2px rule, the folio marks
   it by changing material - the sheet gives way to an inked plate. */

const STEPS: [string, string][] = [
  [
    "Describe it in the register's terms",
    "Subfield, study levels, academic role, contract. The same fields every entry is filed under.",
  ],
  [
    "It joins the register",
    "One measure, one order. No tiers, no boosted placement, no paid position in the list.",
  ],
  [
    "It is found by subfield",
    "Scholars search the field map, not a keyword box. Your post surfaces for the people who do that exact work.",
  ],
];

export const LeafInstitutions: React.FC = () => {
  const { user } = useAuth();
  const { openRegisterModal } = useLoginModal();

  const postCta = { ...btn(T, "accent"), background: T.ground, color: T.accent };

  return (
    <Leaf
      code="§ A-04 · issued to"
      measure={
        <div className={`${P}-plate`} style={{ background: T.accent, color: T.onAccent, padding: "44px 38px" }}>
          <h2 style={display(T, "clamp(30px, 4.4vw, 52px)", T.onAccent)}>Post a position.</h2>

          <p style={{ ...body(T, T.onAccent), fontSize: 17, marginTop: 16, maxWidth: 560 }}>
            A post for an external examiner, a panel member or an hourly-paid lecturer is not a
            vacancy advertisement, and it does not belong in one. Put it in the register and it is
            filed the way the work is described.
          </p>

          <ol style={{ listStyle: "none", margin: "34px 0 0", padding: 0 }}>
            {STEPS.map(([head, note], i) => (
              <li key={head} style={{ borderTop: `1.5px solid ${T.onAccent}`, paddingTop: 14, marginBottom: 20 }}>
                <div style={{ ...mono(T, T.onAccent), marginBottom: 8 }}>{String(i + 1).padStart(2, "0")}</div>
                <div style={display(T, "19px", T.onAccent)}>{head}</div>
                <p style={{ ...body(T, T.onAccent), fontSize: 15, marginTop: 8 }}>{note}</p>
              </li>
            ))}
          </ol>

          <p style={{ ...body(T, T.onAccent), fontSize: 15, marginTop: 28, maxWidth: 540 }}>
            Listing is free while the register is being built. When that changes, it changes in
            public and it changes for everyone at once.
          </p>

          <div style={{ display: "flex", gap: 12, marginTop: 26, flexWrap: "wrap" }}>
            {user ? (
              <Link to={ROUTE_NAMES.PAGES.POST_JOB} className={`${P}-invert`} style={{ ...postCta, textDecoration: "none" }}>
                Post a position
              </Link>
            ) : (
              <button type="button" onClick={openRegisterModal} className={`${P}-invert`} style={postCta}>
                Post a position
              </button>
            )}
            <Link
              to={ROUTE_NAMES.PAGES.CONTACT}
              className={`${P}-ghost`}
              style={{ ...btn(T, "outline"), color: T.onAccent, border: `1.5px solid ${T.onAccent}`, textDecoration: "none" }}
            >
              Ask the desk first
            </Link>
          </div>
        </div>
      }
      margin={
        <div style={{ borderTop: `1.5px solid ${T.ink}`, paddingTop: 14 }}>
          <div style={{ ...mono(T, T.ink), marginBottom: 8 }}>Scholars</div>
          <p style={{ ...body(T), fontSize: 13.5, marginBottom: 14 }}>
            Reading rather than hiring? The register is free, and stays free.
          </p>
          <Link
            to={ROUTE_NAMES.PAGES.JOBS}
            className={`${P}-out`}
            style={{ ...btn(T, "outline"), padding: "10px 16px", fontSize: 9.5, textDecoration: "none" }}
          >
            Back to the register
          </Link>
        </div>
      }
    />
  );
};
